import { Button } from "@/components/_components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/_components/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/_components/select";
import { API_CONFIG } from "@/constants/api";
import { DEFAULT_GREETING, GREETING_PLACEHOLDERS, AGENT_TYPE, AGENT_TYPE_LABELS } from "@/constants/constants";
import { ROUTES } from "@/constants/routes";
import { customInstance } from "@/lib/axios-instance";
import { cn } from "@/lib/utils";
import {
  LANGUAGES,
  SenderPersona,
  SenderPersonaFormData,
} from "@/types/domain/sender-persona";
import { buttonStyles } from "theme/components/button";
import { textareaStyles } from "theme/components/textarea";
import { ChevronLeft, Save, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { AuthorityGuard } from "@/components/auth/AuthorityGuard";

interface SenderPersonaFormProps {
  mode: "create" | "edit" | "view";
  personaId?: string;
  readOnly?: boolean;
}

interface PhoneNumberOption {
  phoneNo: string;
  countryCode?: string;
}

interface AdditionalPhoneNumber {
  countryCode: string;
  channel: "IM" | "VOICE";
  phoneNo: string;
}

export default function SenderPersonaForm({ mode, personaId, readOnly = false }: SenderPersonaFormProps) {
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(mode === "edit");
  const [currentStep, setCurrentStep] = useState(1);
  const [imPhoneOptions, setImPhoneOptions] = useState<PhoneNumberOption[]>([]);
  const [voicePhoneOptions, setVoicePhoneOptions] = useState<PhoneNumberOption[]>([]);
  const [isLoadingImPhones, setIsLoadingImPhones] = useState(false);
  const [isLoadingVoicePhones, setIsLoadingVoicePhones] = useState(false);
  const [additionalImPhones, setAdditionalImPhones] = useState<AdditionalPhoneNumber[]>([]);
  const [additionalVoicePhones, setAdditionalVoicePhones] = useState<AdditionalPhoneNumber[]>([]);

  const [formData, setFormData] = useState<SenderPersonaFormData>({
    agentType: "",
    name: "",
    aiIdentifier: "",
    description: "",
    languagePreference: "English",
    emailFrom: "",
    emailAddress: "",
    tone: "",
    greeting: DEFAULT_GREETING,
    signature: "",
    imPhoneNo: "",
    voicePhoneNo: "",
    imCountryCode: "",
    voiceCountryCode: "",
  });

  useEffect(() => {
    if ((mode === "edit" || mode === "view") && personaId) {
      fetchPersona();
    }
  }, [mode, personaId]);

  useEffect(() => {
    if (formData.agentType && !readOnly) {
      fetchPhoneNumbers('twilio', 'IM');
      fetchPhoneNumbers('awsConnect', 'VOICE');
    }
  }, [formData.agentType, readOnly]);


  const fetchPhoneNumbers = async (intType: 'twilio' | 'awsConnect', phoneType: 'IM' | 'VOICE') => {
    if (!formData.agentType) return;

    try {
      if (phoneType === 'IM') {
        setIsLoadingImPhones(true);
      } else {
        setIsLoadingVoicePhones(true);
      }

      const response = await customInstance<PhoneNumberOption[]>({
        url: API_CONFIG.ENDPOINTS.COMMUNICATION_SEARCH,
        method: "POST",
        data: {
          agentType: formData.agentType,
          intType: intType
        }
      });

      const phoneNumbers = response.data || [];
      
      if (phoneType === 'IM') {
        setImPhoneOptions(phoneNumbers);
      } else {
        setVoicePhoneOptions(phoneNumbers);
      }
    } catch (error: any) {
      console.error(`Error fetching ${phoneType} phone numbers:`, error);
      toast.error(`Failed to load ${phoneType} phone numbers`);
    } finally {
      if (phoneType === 'IM') {
        setIsLoadingImPhones(false);
      } else {
        setIsLoadingVoicePhones(false);
      }
    }
  };

  const fetchPersona = async () => {
    try {
      setIsLoading(true);
      const response = await customInstance<SenderPersona>({
        url: `${API_CONFIG.ENDPOINTS.SENDER_PERSONA}/${personaId}`,
        method: "GET",
      });

      const data = response.data;

      setFormData({
        agentType: data.agentType || "",
        name: data.name,
        aiIdentifier: data.aiIdentifier,
        description: data.description || "",
        languagePreference: data.languagePreference,
        emailFrom: data.emailFrom,
        emailAddress: data.emailAddress,
        tone: data.tone || "",
        greeting: data.greeting || DEFAULT_GREETING,
        signature: data.signature || "",
        imPhoneNo: data.imPhoneNo || "",
        voicePhoneNo: data.voicePhoneNo || "",
        imCountryCode: data.imCountryCode || "",
        voiceCountryCode: data.voiceCountryCode || "",
      });

      // Load additional phone numbers
      if (data.additionalPhoneNumbers && data.additionalPhoneNumbers.length > 0) {
        const imPhones = data.additionalPhoneNumbers.filter(
          phone => phone.channel === "IM" && 
          phone.phoneNo !== data.imPhoneNo // Exclude primary IM phone
        );
        const voicePhones = data.additionalPhoneNumbers.filter(
          phone => phone.channel === "VOICE" && 
          phone.phoneNo !== data.voicePhoneNo // Exclude primary Voice phone
        );
        
        setAdditionalImPhones(imPhones);
        setAdditionalVoicePhones(voicePhones);
      }
    } catch (error: any) {
      console.error("Error fetching sender persona:", error);
      toast.error("Failed to load sender persona");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof SenderPersonaFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        if (!formData.agentType.trim()) {
          toast.error("Agent Type is required");
          return false;
        }
        if (!formData.name.trim()) {
          toast.error("Name is required");
          return false;
        }
        if (!formData.aiIdentifier.trim()) {
          toast.error("AI Identifier is required");
          return false;
        }
        if (!formData.languagePreference) {
          toast.error("Language Preference is required");
          return false;
        }
        return true;
      case 2:
        if (!formData.imPhoneNo?.trim()) {
          toast.error("IM Phone Number is required");
          return false;
        }

        if (!formData.emailFrom.trim()) {
          toast.error("Email From is required");
          return false;
        }
        if (!formData.emailAddress.trim()) {
          toast.error("Email Address is required");
          return false;
        }
        return true;
      case 3:
        if (!formData.tone?.trim()) {
          toast.error("Tone is required");
          return false;
        }
        if (!formData.greeting?.trim()) {
          toast.error("Greeting is required");
          return false;
        }
        // Validate greeting placeholders
        const greetingText = formData.greeting;
        const placeholderRegex = /\{([^}]+)\}/g;
        const foundPlaceholders = [...greetingText.matchAll(placeholderRegex)].map(match => `{${match[1]}}`);
        const invalidPlaceholders = foundPlaceholders.filter(
          placeholder => !GREETING_PLACEHOLDERS.includes(placeholder as any)
        );
        if (invalidPlaceholders.length > 0) {
          toast.error(
            `Invalid placeholders in greeting: ${invalidPlaceholders.join(", ")}. Only ${GREETING_PLACEHOLDERS.join(", ")} are allowed.`
          );
          return false;
        }
        if (!formData.signature?.trim()) {
          toast.error("Signature is required");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 3));
    }
  };

  const handleSubmitClick = () => {
    if (validateStep(3)) {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const addAdditionalImPhone = () => {
    setAdditionalImPhones([...additionalImPhones, { countryCode: "", channel: "IM", phoneNo: "" }]);
  };

  const addAdditionalVoicePhone = () => {
    setAdditionalVoicePhones([...additionalVoicePhones, { countryCode: "", channel: "VOICE", phoneNo: "" }]);
  };

  const removeAdditionalImPhone = (index: number) => {
    setAdditionalImPhones(additionalImPhones.filter((_, i) => i !== index));
  };

  const removeAdditionalVoicePhone = (index: number) => {
    setAdditionalVoicePhones(additionalVoicePhones.filter((_, i) => i !== index));
  };

  const updateAdditionalImPhone = (index: number, phoneNo: string) => {
    const selectedOption = imPhoneOptions.find(opt => opt.phoneNo === phoneNo);
    const updatedPhones = [...additionalImPhones];
    updatedPhones[index] = {
      ...updatedPhones[index],
      phoneNo: phoneNo,
      countryCode: selectedOption?.countryCode || ""
    };
    setAdditionalImPhones(updatedPhones);
  };

  const updateAdditionalVoicePhone = (index: number, phoneNo: string) => {
    const selectedOption = voicePhoneOptions.find(opt => opt.phoneNo === phoneNo);
    const updatedPhones = [...additionalVoicePhones];
    updatedPhones[index] = {
      ...updatedPhones[index],
      phoneNo: phoneNo,
      countryCode: selectedOption?.countryCode || ""
    };
    setAdditionalVoicePhones(updatedPhones);
  };

  const getUsedImCountryCodes = () => {
    const codes = new Set<string>();
    if (formData.imCountryCode) codes.add(formData.imCountryCode);
    additionalImPhones.forEach(phone => {
      if (phone.countryCode) codes.add(phone.countryCode);
    });
    return codes;
  };

  const getUsedVoiceCountryCodes = () => {
    const codes = new Set<string>();
    if (formData.voiceCountryCode) codes.add(formData.voiceCountryCode);
    additionalVoicePhones.forEach(phone => {
      if (phone.countryCode) codes.add(phone.countryCode);
    });
    return codes;
  };

  const getAvailableImPhoneOptions = (currentCountryCode?: string) => {
    const usedCodes = getUsedImCountryCodes();
    return imPhoneOptions.filter(option => 
      !option.countryCode || 
      option.countryCode === currentCountryCode || 
      !usedCodes.has(option.countryCode)
    );
  };

  const getAvailableVoicePhoneOptions = (currentCountryCode?: string) => {
    const usedCodes = getUsedVoiceCountryCodes();
    return voicePhoneOptions.filter(option => 
      !option.countryCode || 
      option.countryCode === currentCountryCode || 
      !usedCodes.has(option.countryCode)
    );
  };

  const handleSubmit = async () => {
    if (!validateStep(1) || !validateStep(2) || !validateStep(3)) return;

    try {
      setIsSaving(true);

      // Prepare additional phone numbers list (excluding primary phones)
      const phoneNumbers: AdditionalPhoneNumber[] = [];
      
      // Add only additional IM phones (not the primary)
      phoneNumbers.push(...additionalImPhones);
      
      // Add only additional Voice phones (not the primary)
      phoneNumbers.push(...additionalVoicePhones);

      const submitData = {
        ...(mode === "edit" && { id: Number(personaId) }),
        ...formData,
        additionalPhoneNumbers: phoneNumbers
      };

      await customInstance({
        url: API_CONFIG.ENDPOINTS.SENDER_PERSONA,
        method: mode === "create" ? "POST" : "PUT",
        data: submitData,
      });

      toast.success(`Sender persona ${mode === "create" ? "created" : "updated"} successfully!`);
      navigate(ROUTES.SENDER_PERSONA);
    } catch (error: any) {
      console.error(`Error ${mode === "create" ? "creating" : "updating"} sender persona:`, error);
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const steps = [
    { number: 1, title: "Persona Details" },
    { number: 2, title: "Outreach Settings" },
    { number: 3, title: "Communication Preferences" },
  ];

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <Button
          variant="ghost"
          onClick={() => navigate(ROUTES.SENDER_PERSONA)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Sender Personas
        </Button>

        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">
            {mode === "create" ? "Add" : mode === "view" ? "View" : "Edit"} Sender Persona
          </h1>
        </div>

        {!readOnly && (
          <div className="flex items-center justify-between mb-8">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center font-semibold",
                      currentStep >= step.number
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-600"
                    )}
                  >
                    {step.number}
                  </div>
                  <span className="text-sm mt-2 text-center">{step.title}</span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      "h-1 flex-1 mx-2",
                      currentStep > step.number ? "bg-blue-600" : "bg-gray-200"
                    )}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>
              {readOnly ? "Sender Persona Details" : `Step ${currentStep}: ${steps[currentStep - 1].title}`}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {(currentStep === 1 || readOnly) && (
              <>
                <div>
                  <label htmlFor="agentType">
                    Agent Type <span className="text-red-500 text-base font-bold">*</span>
                  </label>
                  <Select
                    value={formData.agentType}
                    onValueChange={(value) => handleInputChange("agentType", value)}
                    disabled={mode === "edit" || readOnly}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select agent type" />
                    </SelectTrigger>
                    <SelectContent>
                      {AGENT_TYPE.map((type) => (
                        <SelectItem key={type} value={type}>
                          {AGENT_TYPE_LABELS[type]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(mode === "edit" || readOnly) && (
                    <p className="text-xs text-gray-500 mt-1">
                      Agent type cannot be changed in {mode === "view" ? "view" : "edit"} mode
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="name">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Recruitment Coordinator"
                    disabled={readOnly}
                  />
                </div>

                <div>
                  <label htmlFor="aiIdentifier">
                    AI Identifier <span className="text-red-500 text-base font-bold">*</span>
                  </label>
                  <input
                    id="aiIdentifier"
                    value={formData.aiIdentifier}
                    onChange={(e) =>
                      handleInputChange("aiIdentifier", e.target.value)
                    }
                    placeholder="Eva"
                    disabled={readOnly}
                  />
                </div>

                <div>
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    className={textareaStyles}
                    rows={3}
                    disabled={readOnly}
                  />
                </div>

                <div>
                  <label htmlFor="languagePreference">
                    Language Preference <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="languagePreference"
                    value={formData.languagePreference}
                    onChange={(e) =>
                      handleInputChange("languagePreference", e.target.value)
                    }
                    disabled={readOnly}
                  >
                    {LANGUAGES.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {(currentStep === 2 || readOnly) && (
              <>
                {readOnly && <div className="border-t pt-6 mt-6"><h3 className="text-lg font-semibold mb-4">Outreach Settings</h3></div>}
                <div>
                  <label htmlFor="imPhoneNo">
                    IM Phone Number <span className="text-red-500 text-base font-bold">*</span>
                  </label>
                  {!formData.agentType && !readOnly && (
                    <p className="text-xs text-amber-600 mb-2">
                      Please select an agent type first to load phone numbers
                    </p>
                  )}
                  {readOnly ? (
                    <input
                      id="imPhoneNo"
                      value={formData.imPhoneNo || ""}
                      disabled={true}
                      className="w-full"
                    />
                  ) : (
                    <Select
                      value={formData.imPhoneNo || ""}
                      onValueChange={(value) => {
                        handleInputChange("imPhoneNo", value);
                        const selectedOption = imPhoneOptions.find(opt => opt.phoneNo === value);
                        handleInputChange("imCountryCode", selectedOption?.countryCode || "");
                      }}
                      disabled={isLoadingImPhones || !formData.agentType}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={
                          isLoadingImPhones 
                            ? "Loading..." 
                            : !formData.agentType 
                              ? "Select agent type first"
                              : "Select IM phone number"
                        } />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px] overflow-y-auto">
                        {getAvailableImPhoneOptions(formData.imCountryCode).map((option) => (
                          <SelectItem key={option.phoneNo} value={option.phoneNo}>
                            {option.countryCode ? `${option.countryCode} - ${option.phoneNo}` : option.phoneNo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {!readOnly && formData.imPhoneNo && (
                  <div className="space-y-3">
                    {additionalImPhones.map((phone, index) => (
                      <div key={index} className="flex gap-2 items-start">
                        <div className="flex-1">
                          <label className="text-sm text-gray-600">
                            Additional IM Phone Number {index + 1}
                          </label>
                          <Select
                            value={phone.phoneNo}
                            onValueChange={(value) => updateAdditionalImPhone(index, value)}
                            disabled={isLoadingImPhones || !formData.agentType}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select additional IM phone number" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px] overflow-y-auto">
                              {getAvailableImPhoneOptions(phone.countryCode).map((option) => (
                                <SelectItem key={option.phoneNo} value={option.phoneNo}>
                                  {option.countryCode ? `${option.countryCode} - ${option.phoneNo}` : option.phoneNo}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeAdditionalImPhone(index)}
                          className="mt-6 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addAdditionalImPhone}
                      className="w-full"
                      disabled={!formData.agentType || isLoadingImPhones}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Additional IM Phone Number
                    </Button>
                  </div>
                )}

                {readOnly && additionalImPhones.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Additional IM Phone Numbers</label>
                    {additionalImPhones.map((phone, index) => (
                      <input
                        key={index}
                        value={phone.countryCode ? `${phone.countryCode} - ${phone.phoneNo}` : phone.phoneNo}
                        disabled={true}
                        className="w-full"
                      />
                    ))}
                  </div>
                )}

                <div>
                  <label htmlFor="voicePhoneNo">
                    Voice Phone Number <span className="text-gray-500">(Optional)</span>
                  </label>
                  {!formData.agentType && !readOnly && (
                    <p className="text-xs text-amber-600 mb-2">
                      Please select an agent type first to load phone numbers
                    </p>
                  )}
                  {readOnly ? (
                    <input
                      id="voicePhoneNo"
                      value={formData.voicePhoneNo || "None"}
                      disabled={true}
                      className="w-full"
                    />
                  ) : (
                    <Select
                      value={formData.voicePhoneNo || "none"}
                      onValueChange={(value) => {
                        const phoneValue = value === "none" ? "" : value;
                        handleInputChange("voicePhoneNo", phoneValue);
                        if (phoneValue) {
                          const selectedOption = voicePhoneOptions.find(opt => opt.phoneNo === phoneValue);
                          handleInputChange("voiceCountryCode", selectedOption?.countryCode || "");
                        } else {
                          handleInputChange("voiceCountryCode", "");
                        }
                      }}
                      disabled={isLoadingVoicePhones || !formData.agentType}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={
                          isLoadingVoicePhones 
                            ? "Loading..." 
                            : !formData.agentType 
                              ? "Select agent type first"
                              : "Select voice phone number (optional)"
                        } />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px] overflow-y-auto">
                        <SelectItem value="none">None (Optional)</SelectItem>
                        {getAvailableVoicePhoneOptions(formData.voiceCountryCode).map((option) => (
                          <SelectItem key={option.phoneNo} value={option.phoneNo}>
                            {option.countryCode ? `${option.countryCode} - ${option.phoneNo}` : option.phoneNo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {!readOnly && formData.voicePhoneNo && (
                  <div className="space-y-3">
                    {additionalVoicePhones.map((phone, index) => (
                      <div key={index} className="flex gap-2 items-start">
                        <div className="flex-1">
                          <label className="text-sm text-gray-600">
                            Additional Voice Phone Number {index + 1}
                          </label>
                          <Select
                            value={phone.phoneNo}
                            onValueChange={(value) => updateAdditionalVoicePhone(index, value)}
                            disabled={isLoadingVoicePhones || !formData.agentType}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select additional voice phone number" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px] overflow-y-auto">
                              {getAvailableVoicePhoneOptions(phone.countryCode).map((option) => (
                                <SelectItem key={option.phoneNo} value={option.phoneNo}>
                                  {option.countryCode ? `${option.countryCode} - ${option.phoneNo}` : option.phoneNo}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeAdditionalVoicePhone(index)}
                          className="mt-6 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addAdditionalVoicePhone}
                      className="w-full"
                      disabled={!formData.agentType || isLoadingVoicePhones}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Additional Voice Phone Number
                    </Button>
                  </div>
                )}

                {readOnly && additionalVoicePhones.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Additional Voice Phone Numbers</label>
                    {additionalVoicePhones.map((phone, index) => (
                      <input
                        key={index}
                        value={phone.countryCode ? `${phone.countryCode} - ${phone.phoneNo}` : phone.phoneNo}
                        disabled={true}
                        className="w-full"
                      />
                    ))}
                  </div>
                )}

                <div>
                  <label htmlFor="emailFrom">
                    From Name <span className="text-red-500 text-base font-bold">*</span>
                  </label>
                  <input
                    id="emailFrom"
                    value={formData.emailFrom}
                    onChange={(e) =>
                      handleInputChange("emailFrom", e.target.value)
                    }
                    placeholder="ServiceGlobal Recruitment"
                    disabled={readOnly}
                  />
                </div>

                <div>
                  <label htmlFor="emailAddress">
                    From Email Address <span className="text-red-500 text-base font-bold">*</span>
                  </label>
                  <input
                    id="emailAddress"
                    type="email"
                    value={formData.emailAddress}
                    onChange={(e) =>
                      handleInputChange("emailAddress", e.target.value)
                    }
                    placeholder="gmrp-coordinator@serviceglobal.com"
                    disabled={readOnly}
                  />
                </div>
              </>
            )}

            {(currentStep === 3 || readOnly) && (
              <>
                {readOnly && <div className="border-t pt-6 mt-6"><h3 className="text-lg font-semibold mb-4">Communication Preferences</h3></div>}
                <div>
                  <label htmlFor="tone">
                    Tone <span className="text-red-500 text-base font-bold">*</span>
                  </label>
                  <textarea
                    id="tone"
                    value={formData.tone}
                    onChange={(e) => handleInputChange("tone", e.target.value)}
                    className={textareaStyles}
                    rows={3}
                    disabled={readOnly}
                  />
                </div>

                <div>
                  <label htmlFor="greeting">
                    Greeting <span className="text-red-500 text-base font-bold">*</span>
                  </label>
                  <textarea
                    id="greeting"
                    value={formData.greeting}
                    onChange={(e) =>
                      handleInputChange("greeting", e.target.value)
                    }
                    className={textareaStyles}
                    placeholder={DEFAULT_GREETING}
                    rows={3}
                    disabled={readOnly}
                  />
                  {!readOnly && (
                    <p className="text-xs text-gray-500 mt-1">
                      Allowed placeholders: {GREETING_PLACEHOLDERS.join(", ")}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="signature">
                    Signature <span className="text-red-500 text-base font-bold">*</span>
                  </label>
                  <textarea
                    id="signature"
                    value={formData.signature}
                    onChange={(e) =>
                      handleInputChange("signature", e.target.value)
                    }
                    className={textareaStyles}
                    rows={3}
                    disabled={readOnly}
                  />
                </div>
              </>
            )}

            <div className="flex justify-between pt-4">
              {readOnly ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(ROUTES.SENDER_PERSONA)}
                  className="ml-auto"
                >
                  Back to Sender Personas
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevious}
                    disabled={currentStep === 1}
                  >
                    Previous
                  </Button>
                  {currentStep < 3 ? (
                    <Button type="button" onClick={handleNext}>
                      Next
                    </Button>
                  ) : (
                    <AuthorityGuard authority="ADMIN_SENDER_PERSONA_UPDATE">
                      <button
                        type="button"
                        onClick={handleSubmitClick}
                        disabled={isSaving}
                        className={cn(
                          buttonStyles,
                          mode === "create"
                            ? "bg-green-600 hover:bg-green-700 text-white"
                            : "bg-blue-600 hover:bg-blue-700 text-white"
                        )}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        {isSaving ? "Saving..." : mode === "create" ? "Save Sender Persona" : "Update Sender Persona"}
                      </button>
                    </AuthorityGuard>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
